import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, NonNullableFormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UiService } from '../../core/services/ui.service';
import { AuthService } from '../../core/services/auth.service';
import { EMAIL_REGEX } from '../../core/constants/validation.constants';
import { AppButtonComponent } from '../../shared/components/button/app-button.component';
import { AuthModalShellComponent } from '../../shared/components/auth-modal-shell/auth-modal-shell.component';
import { AuthEmailFieldComponent } from '../../shared/components/auth-email-field/auth-email-field.component';
import { AuthPasswordFieldComponent } from '../../shared/components/auth-password-field/auth-password-field.component';
import { SnackbarService } from '../../core/services/snackbar.service';
import { Router } from '@angular/router';
import { VanityRedirectService } from '../../core/services/vanity-redirect.service';
import { PostLoginRoutingService } from '../../core/services/post-login-routing.service';
import { navigateOffVanityToMainPortal, parseVanitySlugFromLocation } from '../../core/utils/vanity-url.util';

type LoginForm = {
  email: FormControl<string>;
  password: FormControl<string>;
};

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppButtonComponent,
    AuthModalShellComponent,
    AuthEmailFieldComponent,
    AuthPasswordFieldComponent,
  ],
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.scss'],
})
export class LoginModalComponent implements OnDestroy {
  isVisible = false;
  /** Vanity guests sign in here; membership signup is not offered on this host. */
  readonly isVanityHost = !!parseVanitySlugFromLocation();
  isLoading = false;
  errorMessage = '';
  wrongVanityPortalUrl: string | null = null;
  private subscription = new Subscription();

  loginForm: FormGroup<LoginForm>;

  constructor(
    private uiService: UiService,
    private authService: AuthService,
    private fb: NonNullableFormBuilder,
    private snackbarService: SnackbarService,
    private router: Router,
    private vanityRedirect: VanityRedirectService,
    private postLoginRouting: PostLoginRoutingService,
  ) {
    const modalSub = this.uiService.signInModal$.subscribe((visible) => {
      this.isVisible = visible;
    });
    this.subscription.add(modalSub);

    this.loginForm = this.fb.group<LoginForm>({
      email: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(EMAIL_REGEX)],
      }),
      password: this.fb.control('', {
        validators: [Validators.required],
      }),
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.wrongVanityPortalUrl = null;

    const { email, password } = this.loginForm.getRawValue();

    this.authService.login(email, password).subscribe({
      next: () => {
        this.isLoading = false;
        this.closeModal();

        const profile = this.authService.currentUserProfile;
        if (this.vanityRedirect.maybeRedirect(profile)) {
          return;
        }

        const target = this.postLoginRouting.resolveTarget(profile);

        this.router.navigateByUrl(target, { replaceUrl: true });
      },
      error: (err) => {
        this.isLoading = false;
        const body = err?.error;
        if (body?.ErrorCode === 'WrongVanityHost') {
          this.wrongVanityPortalUrl =
            (typeof body?.Data === 'string' && body.Data) ||
            (typeof body?.PortalUrl === 'string' && body.PortalUrl) ||
            null;
          this.errorMessage = 'You cannot login via this link.';
        } else {
          this.wrongVanityPortalUrl = null;
          this.errorMessage = body?.ErrorMessage || 'Login failed';
        }
        this.snackbarService.show(this.errorMessage, 'error');
      },
    });
  }

  closeModal(): void {
    this.uiService.closeSignInModal();
    this.loginForm.reset();
    this.errorMessage = '';
    this.wrongVanityPortalUrl = null;
  }

  openSignUp(): void {
    this.closeModal();
    if (navigateOffVanityToMainPortal('/choose-memberships/register')) {
      return;
    }
    this.router.navigate(['/choose-memberships/register']);
  }

  openResetPassword(): void {
    this.closeModal();
    this.uiService.openResetPasswordModal();
  }

  get isFormInvalid(): boolean {
    return this.loginForm.invalid;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
